import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export interface PrintBookListItem {
  id: number
  title: string
  subtitle: string | null
  bookLabel: string
  status: string
  moduleCount: number
  lessonCount: number
  totalPageCount: number | null
  confirmedLessonCount: number
  totalLessonCount: number
  createdAt: string
  updatedAt: string
}

export interface PrintBookDetail {
  id: number
  title: string
  subtitle: string | null
  bookLabel: string
  description: string | null
  coverImageUrl: string | null
  status: string
  totalPageCount: number | null
  printSettings: string
  modules: PrintBookModule[]
  createdBy: number | null
  createdAt: string
  updatedAt: string
}

export interface PrintBookModule {
  id: number
  moduleId: number
  moduleName: string | null
  sortOrder: number
  lessons: PrintBookLesson[]
}

export interface PrintBookLesson {
  id: number
  lessonId: number
  lessonName: string | null
  sortOrder: number
  pageCount: number | null
  startPage: number | null
  hasMemoPage: boolean
  confirmed: boolean
  confirmedAt: string | null
}

export interface PrintBookCreateRequest {
  title: string
  subtitle?: string
  bookLabel?: string
  description?: string
  printSettings?: string
  modules?: PrintBookModuleRequest[]
}

export interface PrintBookUpdateRequest {
  title?: string
  subtitle?: string
  bookLabel?: string
  description?: string
  status?: string
  printSettings?: string
  modules?: PrintBookModuleRequest[]
}

export interface PrintBookModuleRequest {
  moduleId: number
  sortOrder: number
  lessons: PrintBookLessonRequest[]
}

export interface PrintBookLessonRequest {
  lessonId: number
  sortOrder: number
}

export interface PrintBookPagesUpdateRequest {
  totalPageCount: number
  lessons: {
    lessonId: number
    pageCount: number
    startPage: number
    hasMemoPage: boolean
  }[]
}

// ============================================================================
// API
// ============================================================================

export const printBookApi = {
  getAll: async (): Promise<PrintBookListItem[]> => {
    const { data } = await apiClient.get<ApiResponse<PrintBookListItem[]>>('/admin/print-books')
    return data.data!
  },

  getById: async (id: number): Promise<PrintBookDetail> => {
    const { data } = await apiClient.get<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}`)
    return data.data!
  },

  create: async (request: PrintBookCreateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.post<ApiResponse<PrintBookDetail>>('/admin/print-books', request)
    return data.data!
  },

  update: async (id: number, request: PrintBookUpdateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}`, request)
    return data.data!
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/print-books/${id}`)
  },

  updatePages: async (id: number, request: PrintBookPagesUpdateRequest): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(`/admin/print-books/${id}/pages`, request)
    return data.data!
  },

  confirmLesson: async (bookId: number, lessonId: number, confirmed: boolean): Promise<PrintBookDetail> => {
    const { data } = await apiClient.put<ApiResponse<PrintBookDetail>>(
      `/admin/print-books/${bookId}/lessons/${lessonId}/confirm?confirmed=${confirmed}`
    )
    return data.data!
  },
}
